import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Link2, Unlink, RefreshCw, Smartphone, Download, Share, CheckCircle2, AlertCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useInstall } from "@/lib/pwa";
import { ago } from "@/lib/constants";

const CalendarCard = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const load = () => api.get("/calendar/status").then((r) => setS(r.data));
  useEffect(() => {
    load();
    const c = params.get("calendar");
    if (c === "connected") toast.success("Google Calendar connected");
    if (c === "error") toast.error("Google Calendar connection failed");
    if (c) setParams({}, { replace: true });
  }, []); // eslint-disable-line

  const connect = async () => {
    try { const { data } = await api.get("/calendar/connect"); window.location.href = data.authorization_url; } catch (e) { toast.error(errMsg(e)); }
  };
  const disconnect = async () => { await api.post("/calendar/disconnect"); toast.success("Disconnected"); load(); };
  const sync = async () => {
    setBusy(true);
    try { const { data } = await api.post("/calendar/sync"); toast.success(`Synced · ${data.pushed} pushed, ${data.rescheduled} moved, ${data.cancelled} cancelled`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!s) return null;
  return (
    <div data-testid="calendar-card" className="rounded-3xl border border-neutral-200 bg-white p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#1F9E7A] text-white"><CalendarDays className="h-6 w-6" /></div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold">Google Calendar sync</h2>
          <p className="mt-1 text-sm text-neutral-500">Meetings you book are added to your Google Calendar with the client invited. Time changes, cancellations and RSVPs made in Google come back into the CRM every 15 minutes.</p>
        </div>
      </div>
      {!s.configured && (
        <p data-testid="calendar-not-configured" className="mt-4 flex items-start gap-2 rounded-2xl bg-[#FBF5C9] px-4 py-3 text-sm text-[#7A6A00]">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />Waiting for Google OAuth keys (GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET). Your admin needs to add them before you can connect.
        </p>
      )}
      {s.connected ? (
        <div className="mt-4 space-y-3">
          <p data-testid="calendar-connected-email" className="flex items-center gap-2 text-sm text-[#137A5E]"><CheckCircle2 className="h-4 w-4" />Connected as <b>{s.google_email}</b> · {s.synced_meetings} meetings synced{s.last_sync_at ? ` · last pull ${ago(s.last_sync_at)}` : ""}</p>
          <div className="flex flex-wrap gap-2">
            <button data-testid="calendar-sync-btn" disabled={busy} onClick={sync} className="flex h-10 items-center gap-1.5 rounded-full bg-[#232323] px-4 text-sm font-semibold text-white disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />Sync now</button>
            <button data-testid="calendar-disconnect-btn" onClick={disconnect} className="flex h-10 items-center gap-1.5 rounded-full border border-neutral-300 px-4 text-sm text-neutral-600"><Unlink className="h-4 w-4" />Disconnect</button>
          </div>
        </div>
      ) : (
        <button data-testid="calendar-connect-btn" disabled={!s.configured} onClick={connect} className="mt-4 flex h-11 items-center gap-2 rounded-full bg-[#1F9E7A] px-5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40">
          <Link2 className="h-4 w-4" />Connect Google Calendar
        </button>
      )}
    </div>
  );
};

const InstallCard = () => {
  const { canInstall, installed, isIOS, promptInstall } = useInstall();
  return (
    <div data-testid="install-card" className="rounded-3xl border border-[#232323] bg-[#232323] p-6 text-white">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#F5E04F] text-[#151515]"><Smartphone className="h-6 w-6" /></div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold">Install on your phone</h2>
          <p className="mt-1 text-sm text-white/70">Add the CRM to your home screen. It opens full-screen, and today's queue stays readable when you have no signal.</p>
        </div>
      </div>
      {installed ? (
        <p data-testid="install-status-installed" className="mt-4 flex items-center gap-2 text-sm text-[#F5E04F]"><CheckCircle2 className="h-4 w-4" />Installed — you're using the app.</p>
      ) : canInstall ? (
        <button data-testid="install-card-btn" onClick={promptInstall} className="mt-4 flex h-11 items-center gap-2 rounded-full bg-[#F5E04F] px-5 text-sm font-semibold text-[#151515]"><Download className="h-4 w-4" />Install app</button>
      ) : (
        <p data-testid="install-instructions" className="mt-4 flex items-start gap-2 rounded-2xl bg-white/10 px-4 py-3 text-sm text-white/80">
          <Share className="mt-0.5 h-4 w-4 shrink-0 text-[#F5E04F]" />
          {isIOS ? "On iPhone: tap Share in Safari → “Add to Home Screen”." : "On Android/Chrome: open the browser menu (⋮) → “Install app” or “Add to Home screen”."}
        </p>
      )}
    </div>
  );
};

export default function Settings() {
  return (
    <div className="space-y-5">
      <div><h1 className="font-display text-2xl font-bold text-[#151515]">Settings</h1><p className="text-sm text-neutral-500">Calendar connection and mobile app.</p></div>
      <div className="grid gap-4 lg:grid-cols-2"><CalendarCard /><InstallCard /></div>
    </div>
  );
}
